import React, { useState } from 'react'
import { http } from '../../api/http'
import { useAuth } from '../../auth/store'
import { CompanyJoinForm } from './CompanyJoinForm'
import { logger } from '../../utils/logger'

interface RegistrationFormProps {
  onSuccess: (token: string, user: any) => void
  onError: (message: string) => void
  onSwitchToLogin?: () => void
  onSwitchToPasswordReset?: () => void
}

interface RegisterFormData {
  name: string
  surname: string
  email: string
  company_name: string
  password: string
  confirmPassword: string
  phone?: string
  company_iin?: string
  company_email?: string
}

interface CompanyInfo {
  id: string
  title: string
  email?: string
  iin?: string
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({ onSuccess, onError, onSwitchToLogin, onSwitchToPasswordReset }) => {
  const { mode: authMode, initData } = useAuth()
  const [mode, setMode] = useState<'choice' | 'new_company' | 'existing_company'>('choice')
  const [currentStep, setCurrentStep] = useState<1 | 2>(1) // Шаг регистрации для новой компании
  const [isLoading, setIsLoading] = useState(false)
  const [foundCompanies, setFoundCompanies] = useState<CompanyInfo[]>([])
  const [selectedCompany, setSelectedCompany] = useState<CompanyInfo | null>(null)
  const [isLoadingCompany, setIsLoadingCompany] = useState(false)
  const [companyAutoFilled, setCompanyAutoFilled] = useState(false)
  const [companyStatus, setCompanyStatus] = useState<string>('')
  const [companyNotFound, setCompanyNotFound] = useState(false)

  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    surname: '',
    email: '',
    company_name: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company_iin: '',
    company_email: ''
  })

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateINN = (inn: string): boolean => {
    return /^\d{10,12}$/.test(inn)
  }

  const validateName = (name: string): boolean => {
    return name.trim().length >= 2
  }

  const validatePassword = (password: string): boolean => {
    return password.length >= 6
  }

  const formatPhoneNumber = (value: string): string => {
    // Убираем все нецифровые символы
    const numbers = value.replace(/\D/g, '')
    
    // Если начинается с 8, заменяем на 7
    let formattedNumbers = numbers
    if (numbers.startsWith('8')) {
      formattedNumbers = '7' + numbers.slice(1)
    }
    
    // Если не начинается с 7, добавляем 7 в начало
    if (!formattedNumbers.startsWith('7') && formattedNumbers.length > 0) {
      formattedNumbers = '7' + formattedNumbers
    }
    
    // Ограничиваем длину (7 + 10 цифр = 11 максимум)
    formattedNumbers = formattedNumbers.slice(0, 11)
    
    // Форматируем как +7 (999) 123-45-67
    if (formattedNumbers.length === 0) return ''
    if (formattedNumbers.length === 1) return `+${formattedNumbers}`
    if (formattedNumbers.length <= 4) return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1)}`
    if (formattedNumbers.length <= 7) return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1, 4)}) ${formattedNumbers.slice(4)}`
    if (formattedNumbers.length <= 9) return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1, 4)}) ${formattedNumbers.slice(4, 7)}-${formattedNumbers.slice(7)}`
    
    return `+${formattedNumbers.slice(0, 1)} (${formattedNumbers.slice(1, 4)}) ${formattedNumbers.slice(4, 7)}-${formattedNumbers.slice(7, 9)}-${formattedNumbers.slice(9)}`
  }

  const getFieldValidationState = (field: string, value: string): 'valid' | 'invalid' | 'neutral' => {
    if (!value.trim()) return 'neutral'
    
    switch (field) {
      case 'name':
      case 'surname':
        return validateName(value) ? 'valid' : 'invalid'
      case 'email':
        return validateEmail(value) ? 'valid' : 'invalid'
      case 'password':
        return validatePassword(value) ? 'valid' : 'invalid'
      case 'confirmPassword':
        return value === formData.password ? 'valid' : 'invalid'
      case 'company_iin':
        return validateINN(value) ? 'valid' : 'invalid'
      case 'company_email':
        return validateEmail(value) ? 'valid' : 'invalid'
      case 'phone':
        // Телефон валиден, если содержит 11 цифр (российский номер)
        const phoneNumbers = value.replace(/\D/g, '')
        return phoneNumbers.length === 11 ? 'valid' : (phoneNumbers.length > 0 ? 'invalid' : 'neutral')
      default:
        return 'neutral'
    }
  }

  // Проверяет, является ли статус компании неактивным
  const isCompanyStatusInactive = (status: string): boolean => {
    if (!status) return false
    
    const inactiveStatuses = [
      'LIQUIDATING',    // В процессе ликвидации
      'LIQUIDATED',     // Ликвидирована
      'BANKRUPT',       // Банкрот
      'REORGANIZING',   // Реорганизация
      'SUSPENDED',      // Приостановлена
      'DISSOLVED',      // Прекращена
      'TERMINATED',     // Прекращена
      'INACTIVE',       // Неактивна
      'CLOSED'          // Закрыта
    ]
    
    return inactiveStatuses.includes(status.toUpperCase())
  }

  // Возвращает человекочитаемый текст для статуса компании
  const getStatusDisplayText = (status: string): string => {
    if (!status) return 'Неизвестный статус'
    
    const statusTexts: Record<string, string> = {
      'ACTIVE': 'Активна',
      'LIQUIDATING': 'Ликвидируется',
      'LIQUIDATED': 'Ликвидирована',
      'BANKRUPT': 'Банкрот',
      'REORGANIZING': 'Реорганизация',
      'SUSPENDED': 'Приостановлена',
      'DISSOLVED': 'Прекращена',
      'TERMINATED': 'Прекращена',
      'INACTIVE': 'Неактивна',
      'CLOSED': 'Закрыта'
    }
    
    return statusTexts[status.toUpperCase()] || status
  }

  const autoFillCompanyName = async (inn: string) => {
    if (!validateINN(inn)) {
      return
    }

    setIsLoadingCompany(true)
    setCompanyAutoFilled(false)
    setCompanyStatus('')
    setCompanyNotFound(false)

    try {
      logger.log('🔍 Поиск компании по ИНН:', inn)
      
      // Используем DaData API для поиска компании по ИНН (как в старом проекте)
      const response = await http.post('/web_auth/company-by-inn', {
        inn: inn
      })

      if (response.data.success) {
        // Определяем название компании - используем company_name или full_name
        const companyName = response.data.company_name || response.data.full_name || ''
        
        if (companyName) {
          // Успешно найдена компания
          logger.log('✅ Найдена компания:', companyName, 'Статус:', response.data.status)
          
          setFormData(prev => ({
            ...prev,
            company_name: companyName
          }))
          setCompanyAutoFilled(true)
          setCompanyStatus(response.data.status || '')
          setCompanyNotFound(false)
          setFieldErrors(prev => ({ ...prev, company_iin: '', company_name: '' }))

          // Показываем предупреждение для неактивных статусов
          if (response.data.status && isCompanyStatusInactive(response.data.status)) {
            const statusText = getStatusDisplayText(response.data.status)
            onError(`⚠️ Внимание: компания "${companyName}" имеет статус "${statusText}". Рекомендуется проверить актуальность данных.`)
          }
        } else {
          // Компания найдена, но без названия (редкий случай)
          logger.log('⚠️ Компания найдена, но без названия. Статус:', response.data.status)
          
          setFormData(prev => ({
            ...prev,
            company_name: ''
          }))
          setCompanyAutoFilled(false)
          setCompanyStatus(response.data.status || '')
          setCompanyNotFound(true)
          
          if (response.data.status && isCompanyStatusInactive(response.data.status)) {
            const statusText = getStatusDisplayText(response.data.status)
            setFieldErrors(prev => ({ 
              ...prev, 
              company_name: `Компания найдена, но имеет статус "${statusText}". Введите название вручную.`
            }))
          } else {
            setFieldErrors(prev => ({ 
              ...prev, 
              company_name: 'Компания найдена, но название не определено. Введите вручную.'
            }))
          }
        }
      } else {
        // Компания не найдена
        logger.log('⚠️ Компания не найдена:', response.data.error)
        
        setFormData(prev => ({
          ...prev,
          company_name: ''
        }))
        setCompanyAutoFilled(false)
        setCompanyStatus('')
        setCompanyNotFound(true)
        
        if (response.data.error) {
          setFieldErrors(prev => ({ 
            ...prev, 
            company_name: response.data.error 
          }))
        }
      }
    } catch (error: any) {
      logger.error('❌ Ошибка при поиске компании:', error)
      
      setFormData(prev => ({
        ...prev,
        company_name: ''
      }))
      setCompanyAutoFilled(false)
      setCompanyStatus('')
      setCompanyNotFound(true)
      
      onError('Не удалось найти компанию по ИНН. Введите название вручную.')
    } finally {
      setIsLoadingCompany(false)
    }
  }

  const searchCompanies = async () => {
    if (!formData.company_iin || !validateINN(formData.company_iin)) {
      setFieldErrors({ company_iin: 'ИНН должен содержать 10-12 цифр' })
      return
    }

    setIsLoading(true)
    setFieldErrors({})

    try {
      const response = await http.post('/web_auth/search-companies', {
        company_iin: formData.company_iin
      })

      if (response.data.success && response.data.companies?.length > 0) {
        setFoundCompanies(response.data.companies)
        setSelectedCompany(response.data.companies[0])
        setFormData(prev => ({
          ...prev,
          company_name: response.data.companies[0].title
        }))
      } else {
        setFoundCompanies([])
        onError('Компания с указанным ИНН не найдена')
      }
    } catch (error: any) {
      onError(error.response?.data?.message || 'Ошибка при поиске компании')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()

    // Для нового режима регистрации проверяем только данные пользователя на финальном шаге
    if (mode === 'new_company') {
      if (!validateUserData()) return
    }



    setIsLoading(true)

    try {
      const registrationData = {
        name: formData.name.trim(),
        surname: formData.surname.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        company_name: mode === 'new_company' ? formData.company_name.trim() : selectedCompany?.title,
        phone: formData.phone?.trim(),
        company_iin: formData.company_iin?.trim(),
        company_email: formData.company_email?.trim()
      }

      let response
      
      // ИСПРАВЛЕНО: Если есть initData, то это всегда Telegram режим
      if (initData) {
        // Telegram режим - отправляем на /auth/register с initData
        logger.info('🔄 Регистрация через Telegram MiniApp с initData')
        response = await http.post('/register', {
          ...registrationData,
          initData: initData
        })
      } else {
        // Браузерный режим - отправляем на /web_auth/register
        logger.info('🔄 Регистрация через веб-браузер')
        response = await http.post('/web_auth/register', registrationData)
      }

      if (response.data.success && response.data.token) {
        onSuccess(response.data.token, response.data.user)
      } else {
        onError(response.data.message || 'Ошибка регистрации')
      }

    } catch (error: any) {
      onError(error.response?.data?.message || 'Произошла ошибка при регистрации')
    } finally {
      setIsLoading(false)
    }
  }

  const clearMessages = () => {
    setFieldErrors({})
  }

  const getFieldError = (field: string) => fieldErrors[field]

  const getInputStyle = (field: string, value: string, isFocused: boolean = false) => {
    const hasError = getFieldError(field)
    if (hasError) return { ...styles.input, ...styles.inputError }
    
    if (isFocused) return { ...styles.input, ...styles.inputFocus }
    
    const validationState = getFieldValidationState(field, value)
    switch (validationState) {
      case 'valid':
        return { ...styles.input, ...styles.inputValid }
      case 'invalid':
        return { ...styles.input, ...styles.inputInvalid }
      default:
        return styles.input
    }
  }

  // Иконка глаза (показать пароль)
  const EyeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  )

  // Иконка перечеркнутого глаза (скрыть пароль)
  const EyeOffIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )

  // Валидация данных компании (шаг 1)
  const validateCompanyData = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.company_iin || !validateINN(formData.company_iin)) {
      errors.company_iin = 'ИНН должен содержать 10-12 цифр'
    }
    if (!formData.company_name.trim()) {
      errors.company_name = 'Введите название компании'
    }
    if (!formData.company_email || !validateEmail(formData.company_email)) {
      errors.company_email = 'Введите корректный email компании'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Валидация данных пользователя (шаг 2)
  const validateUserData = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) errors.name = 'Введите имя'
    if (!formData.surname.trim()) errors.surname = 'Введите фамилию'
    if (!validateEmail(formData.email)) errors.email = 'Введите корректный email'
    if (!formData.password || formData.password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов'
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Переход к следующему шагу
  const handleNextStep = () => {
    if (currentStep === 1 && validateCompanyData()) {
      setCurrentStep(2)
    }
  }

  // Возврат к предыдущему шагу
  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1)
      clearMessages() // Очищаем ошибки при возврате
    }
  }

  // Сброс состояния при выборе нового типа регистрации
  const handleModeChange = (newMode: 'choice' | 'new_company' | 'existing_company') => {
    setMode(newMode)
    setCurrentStep(1)
    setCompanyAutoFilled(false)
    setCompanyStatus('')
    setCompanyNotFound(false)
    clearMessages()
  }

  // Стили
  const styles = {
    formGroup: {
      marginBottom: '1rem'
    },
    formGroupRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      color: '#374151',
      fontWeight: 500,
      marginBottom: '0.25rem',
      fontSize: '0.875rem'
    },
    input: {
      width: '100%',
      padding: '0.625rem 0.875rem',
      border: '2px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      transition: 'all 0.2s ease',
      background: '#f3f4f6',
      boxSizing: 'border-box' as const,
      outline: 'none',
      color: '#374151'
    },
    inputError: {
      borderColor: '#ef4444',
      background: '#fef2f2'
    },
    inputValid: {
      borderColor: '#22c55e',
      background: '#f0fdf4'
    },
    inputInvalid: {
      borderColor: '#ef4444',
      background: '#fef2f2'
    },
    inputFocus: {
      borderColor: '#3b82f6',
      background: '#ffffff',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    passwordContainer: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center'
    },
    passwordToggle: {
      position: 'absolute' as const,
      right: '0.75rem',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      ':hover': {
        color: '#374151',
        background: '#f3f4f6'
      }
    },
    errorMessage: {
      color: '#ef4444',
      fontSize: '0.8rem',
      marginTop: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    button: {
      width: '100%',
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '0.9rem',
      cursor: 'pointer',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    buttonSecondary: {
      background: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },

    backButton: {
      background: 'transparent',
      color: '#6b7280',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.875rem',
      marginBottom: '1rem'
    },
    progressIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem',
      gap: '0.75rem'
    },
    progressStep: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.75rem',
      height: '1.75rem',
      borderRadius: '50%',
      fontSize: '0.8rem',
      fontWeight: 600,
      transition: 'all 0.2s ease'
    },
    progressStepActive: {
      background: '#667eea',
      color: 'white'
    },
    progressStepInactive: {
      background: '#e5e7eb',
      color: '#6b7280'
    },
    progressLine: {
      height: '2px',
      width: '2.5rem',
      background: '#e5e7eb',
      borderRadius: '1px'
    },
    progressLineActive: {
      background: '#667eea'
    }
  }

  // Режим присоединения к существующей компании
  if (mode === 'existing_company') {
    return (
      <CompanyJoinForm
        onSuccess={(message: string) => onSuccess('', { message })}
        onError={onError}
        onBack={() => setMode('choice')}
        onLogin={onSwitchToLogin}
        onForgotPassword={onSwitchToPasswordReset}
      />
    )
  }

  if (mode === 'choice') {
    return (
      <>
        <style>{`
          input::placeholder {
            color: #9ca3af !important;
            opacity: 1;
          }
        `}</style>
        <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'white',
        borderRadius: '24px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.5rem',
        transform: 'scale(1)',
        transition: 'transform 0.3s ease'
      }}>
        {/* Карточка "Создать новую компанию" */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #60a5fa 0%, #6366f1 100%)',
            color: 'white',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            transform: 'translateY(0)'
          }}
          onClick={() => handleModeChange('new_company')}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)'
            e.currentTarget.style.transform = 'translateY(-4px)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            e.currentTarget.style.background = 'linear-gradient(135deg, #60a5fa 0%, #6366f1 100%)'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🏢</div>
          <div style={{ 
            textAlign: 'center', 
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: '1.4'
          }}>
            Зарегистрировать новую компанию в системе
          </div>
        </div>

        {/* Карточка "Присоединиться к компании" */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)',
            color: 'white',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            transform: 'translateY(0)'
          }}
          onClick={() => handleModeChange('existing_company')}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf6 0%, #e11d48 100%)'
            e.currentTarget.style.transform = 'translateY(-4px)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            e.currentTarget.style.background = 'linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🤝</div>
          <div style={{ 
            textAlign: 'center', 
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: '1.4'
          }}>
            Найти и присоединиться к существующей компании
          </div>
        </div>

        {/* Ссылка "Уже есть аккаунт? Войти" */}
        <div style={{ 
          marginTop: '1rem', 
          textAlign: 'center', 
          fontSize: '0.875rem' 
        }}>
          <span style={{ color: '#6b7280' }}>
            Уже есть аккаунт?{' '}
          </span>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#2563eb',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none',
              fontSize: '0.875rem'
            }}
            onClick={() => {
              if (onSwitchToLogin) {
                onSwitchToLogin()
              } else {
                // Fallback для случаев когда функция не передана
                alert('Переход к форме входа')
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none'
            }}
          >
            Войти
          </button>
        </div>
      </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        input::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }
      `}</style>
      <div style={{
      width: '100%',
      maxWidth: '420px',
      background: 'white',
      borderRadius: '24px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: '2rem',
      transform: 'scale(1)',
      transition: 'transform 0.3s ease'
    }}>
      {/* Индикатор прогресса для нового режима регистрации */}
      {mode === 'new_company' && (
        <div style={{...styles.progressIndicator, marginBottom: '1rem'}}>
          <div style={{
            ...styles.progressStep,
            ...(currentStep === 1 ? styles.progressStepActive : styles.progressStepInactive)
          }}>
            1
          </div>
          <div style={{
            ...styles.progressLine,
            ...(currentStep === 2 ? styles.progressLineActive : {})
          }}></div>
          <div style={{
            ...styles.progressStep,
            ...(currentStep === 2 ? styles.progressStepActive : styles.progressStepInactive)
          }}>
            2
          </div>
        </div>
      )}

      <form onSubmit={handleRegister}>




        {mode === 'new_company' && (
          <>
            {/* Шаг 1: Данные компании */}
            {currentStep === 1 && (
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>ИНН компании *</label>
                  <input
                    type="text"
                    value={formData.company_iin || ''}
                    onChange={(e) => {
                      const inn = e.target.value.replace(/\D/g, '').slice(0, 12)
                      setFormData(prev => ({ ...prev, company_iin: inn }))
                      clearMessages()
                      
                      // Автозаполнение при вводе 10 или 12 цифр
                      if (inn.length === 10 || inn.length === 12) {
                        autoFillCompanyName(inn)
                      } else {
                        setCompanyAutoFilled(false)
                        setCompanyStatus('')
                        setCompanyNotFound(false)
                        setFormData(prev => ({ ...prev, company_name: '' }))
                      }
                    }}
                    style={getInputStyle('company_iin', formData.company_iin || '')}
                    placeholder="1234567890"
                    maxLength={12}
                    required
                  />
                  {getFieldError('company_iin') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('company_iin')}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Название компании *
                    {companyAutoFilled && !isCompanyStatusInactive(companyStatus) && (
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#10b981',
                        fontWeight: 'normal',
                        marginLeft: '0.5rem'
                      }}>
                        ✓ Автозаполнено
                      </span>
                    )}
                    {companyAutoFilled && isCompanyStatusInactive(companyStatus) && (
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#ef4444',
                        fontWeight: 'normal',
                        marginLeft: '0.5rem'
                      }}>
                        ⚠️ {getStatusDisplayText(companyStatus)}
                      </span>
                    )}
                    {isLoadingCompany && (
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontWeight: 'normal',
                        marginLeft: '0.5rem'
                      }}>
                        ⏳ Поиск...
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, company_name: e.target.value }))
                      clearMessages()
                      setCompanyAutoFilled(false)
                      setCompanyStatus('')
                    }}
                    style={{
                      ...getInputStyle('company_name', formData.company_name),
                      ...(companyAutoFilled && !isCompanyStatusInactive(companyStatus) ? {
                        background: '#f0fdf4',
                        borderColor: '#22c55e'
                      } : {}),
                      ...(companyAutoFilled && isCompanyStatusInactive(companyStatus) ? {
                        background: '#fef2f2',
                        borderColor: '#ef4444'
                      } : {})
                    }}
                    placeholder={companyNotFound ? "Введите название компании вручную" : "ООО «Пример»"}
                    readOnly={isLoadingCompany || (companyAutoFilled && !getFieldError('company_name'))}
                    required
                  />
                  {!formData.company_iin && !companyNotFound && (
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#6b7280',
                      marginTop: '0.125rem'
                    }}>
                      Название будет заполнено автоматически после ввода ИНН
                    </div>
                  )}
                  {getFieldError('company_name') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('company_name')}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email компании *</label>
                  <input
                    type="email"
                    value={formData.company_email || ''}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, company_email: e.target.value }))
                      clearMessages()
                    }}
                    style={getInputStyle('company_email', formData.company_email || '')}
                    placeholder="info@company.com"
                    required
                  />
                  {getFieldError('company_email') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('company_email')}
                    </div>
                  )}
                </div>

                {/* Кнопка "Далее" для первого шага */}
                <button
                  type="button"
                  onClick={handleNextStep}
                  style={styles.button}
                >
                  <span>➡️</span> Далее
                </button>
              </div>
            )}

            {/* Шаг 2: Ваши данные */}
            {currentStep === 2 && (
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={styles.formGroupRow}>
                  <div>
                    <label style={styles.label}>Имя *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, name: e.target.value }))
                        clearMessages()
                      }}
                      style={getInputStyle('name', formData.name)}
                      placeholder="Иван"
                      required
                    />
                    {getFieldError('name') && (
                      <div style={styles.errorMessage}>
                        <span>⚠️</span> {getFieldError('name')}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={styles.label}>Фамилия *</label>
                    <input
                      type="text"
                      value={formData.surname}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, surname: e.target.value }))
                        clearMessages()
                      }}
                      style={getInputStyle('surname', formData.surname)}
                      placeholder="Иванов"
                      required
                    />
                    {getFieldError('surname') && (
                      <div style={styles.errorMessage}>
                        <span>⚠️</span> {getFieldError('surname')}
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, email: e.target.value }))
                      clearMessages()
                    }}
                    style={getInputStyle('email', formData.email)}
                    placeholder="ivan@example.com"
                    required
                  />
                  {getFieldError('email') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('email')}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Телефон</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value)
                      setFormData(prev => ({ ...prev, phone: formatted }))
                      clearMessages()
                    }}
                    style={getInputStyle('phone', formData.phone || '')}
                    placeholder="+7 (999) 123-45-67"
                  />
                  {getFieldError('phone') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('phone')}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Пароль *</label>
                  <div style={styles.passwordContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, password: e.target.value }))
                        clearMessages()
                      }}
                      style={{
                        ...getInputStyle('password', formData.password || ''),
                        paddingRight: '2.5rem'
                      }}
                      placeholder="Минимум 6 символов"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={styles.passwordToggle}
                      tabIndex={-1}
                      title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {getFieldError('password') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('password')}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Подтвердите пароль *</label>
                  <div style={styles.passwordContainer}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))
                        clearMessages()
                      }}
                      style={{
                        ...getInputStyle('confirmPassword', formData.confirmPassword),
                        paddingRight: '2.5rem'
                      }}
                      placeholder="Повторите пароль"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={styles.passwordToggle}
                      tabIndex={-1}
                      title={showConfirmPassword ? "Скрыть пароль" : "Показать пароль"}
                    >
                      {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {getFieldError('confirmPassword') && (
                    <div style={styles.errorMessage}>
                      <span>⚠️</span> {getFieldError('confirmPassword')}
                    </div>
                  )}
                </div>

                {/* Информация о пароле для Telegram Mini App */}
                {authMode === 'telegram' && (
                  <div style={{
                    background: '#e0f2fe',
                    border: '1px solid #0ea5e9',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1rem', marginTop: '0.125rem' }}>ℹ️</span>
                      <div>
                        <div style={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: '#0369a1',
                          marginBottom: '0.25rem'
                        }}>
                          Пароль для браузерной версии
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#075985',
                          lineHeight: '1.3'
                        }}>
                          Этот пароль создается для входа в браузерную версию системы. Данные для входа будут отправлены вам на почту.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Кнопка регистрации для второго шага */}
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    ...styles.button,
                    ...(isLoading ? styles.buttonDisabled : {}),
                    width: '100%'
                  }}
                >
                  {isLoading ? (
                    <>
                      <span>⏳</span> Регистрируемся...
                    </>
                  ) : (
                    <>
                      <span>📝</span> Зарегистрироваться
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}


      </form>

      {/* Кнопка "Назад к выбору" внизу страницы */}
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <button
          type="button"
          onClick={() => handleModeChange('choice')}
          style={{
            ...styles.backButton,
            marginBottom: 0
          }}
        >
          ← Назад к выбору
        </button>
      </div>
    </div>
    </>
  )
}
